// src/components/resources/guides/CreativeSpecsGuide.tsx

'use client'

import { useEffect } from 'react'
import { ResourceLayout } from '~/components/resources/ResourceLayout'
import { GlossaryFaqs } from '~/components/resources/glossary/GlossaryFaqs'
import { SpecsOverview } from '~/components/resources/guides/specs/SpecsOverview'
import { SpecsTable } from '~/components/resources/guides/specs/SpecsTable'
import type { SpecsGuide } from '~/lib/resources/types/guides'

interface CreativeSpecsGuideProps {
  guide: SpecsGuide
}

export function CreativeSpecsGuide({ guide }: CreativeSpecsGuideProps): JSX.Element {
  useEffect(() => {
    // Handle hash-based navigation to a section (ids come from the guide data).
    const hash = window.location.hash
    if (hash) {
      const target = document.getElementById(hash.slice(1))
      target?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [])

  // Render every section the guide actually defines, rather than assuming a
  // fixed four-section Meta layout. A specs guide is valid with one or more
  // sections that each carry format specifications.
  const sections = (guide.sections ?? [])
    .filter((section) => section?.content?.formats?.length)
    .map((section, index) => ({
      ...section,
      // `id` is optional on the section model; fall back to a stable index-based
      // anchor so quick-nav and hash navigation always have a target.
      anchorId: section.id ?? `spec-section-${index}`,
    }))

  if (sections.length === 0) {
    return (
      <div className="p-8 text-center">
        <p className="text-gray-600 dark:text-gray-400">
          Guide sections are not properly configured.
        </p>
      </div>
    )
  }

  return (
    <ResourceLayout
      title={guide.title}
      description={guide.description}
      showHeader={false}
      suppressFallbackHeading
    >
      <div className="space-y-16">
        {/* Overview Section — driven by the guide's own title/description/sections */}
        <section>
          <SpecsOverview
            title={guide.title}
            description={guide.description}
            sections={sections.map((section) => ({
              id: section.anchorId,
              title: section.title,
              description: section.description,
            }))}
          />
        </section>

        {/* One section per guide-defined spec section */}
        {sections.map((section) => (
          <section key={section.anchorId} id={section.anchorId} className="scroll-mt-24">
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
                {section.title}
              </h2>
              {section.description ? (
                <p className="mt-4 text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
                  {section.description}
                </p>
              ) : null}
            </div>
            <SpecsTable
              formats={section.content.formats}
              guideSlug={guide.slug}
              platform={guide.config.defaultPlatform}
            />
          </section>
        ))}

        {/* FAQs — the FAQPage JSON-LD emitted by WithResourceInstanceStructuredData
            describes `guide.faqs`, so this section keeps that structured data
            backed by visible on-page content (same pattern as ContentGuideGuide). */}
        {guide.faqs && guide.faqs.length > 0 && (
          <section id="faq" className="scroll-mt-24">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
              Frequently Asked Questions
            </h2>
            <GlossaryFaqs
              className="mt-8"
              items={guide.faqs
                .filter((faq): faq is typeof faq & { answer: string } => typeof faq.answer === 'string')
                .map((faq) => ({
                  question: faq.question,
                  answer: faq.answer,
                }))}
              idPrefix={guide.slug}
            />
          </section>
        )}
      </div>
    </ResourceLayout>
  )
}
